import {useQueryClient} from '@tanstack/react-query'
import {Ripple} from 'primereact/ripple'
import React, {useCallback, useMemo, useRef} from 'react'
import {ReactComponent as FcSettings} from '../../assets/icons/FcSettings.svg'

import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {Avatar} from 'primereact/avatar'
import {Badge} from 'primereact/badge'
import {Button} from 'primereact/button'
import {Panel} from 'primereact/panel'
import {TabPanel, TabView} from 'primereact/tabview'
import {Tag} from 'primereact/tag'
import {Tooltip} from 'primereact/tooltip'
import {useHistory, useLocation} from 'react-router-dom'
import NotifyImg from '../../assets/images/undraw_notify.svg'
import {HTTP_STATUS_OK} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryActionTypes} from '../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {upperCase} from '../../utils/CommonUtils'
import useWindowSize from '../../utils/useWindowSize'
import {headerNotificationList} from '../../views/ViewsContants'
import {useAuth} from '../authentication/AuthProvider'
import {setRoomMessage} from '../chat/ChatService'
import Map from '../map/Map'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {convertKeyToLabel} from '../table-utils/TableUtils'
import {
    NotificationStatus,
    NotificationTypes,
    severityNotification,
} from './NotificationConstant'
import {clearAllNotifications} from './NotificationEndPoint'
import {useNotificationContext} from './NotificationProvider'
import {showNotification} from './NotificationService'
import {NotificationSetting} from './NotificationSetting'
import {
    useCustomPageNotificationsFetchMore,
    useReadOrDiscardNotification,
} from './query-keys/NotificationKeysManager'
import './style/style.css'
const PATH = 'notification'
const QueryKey = 'Notification'

const notificationTypeKey = [
    NotificationTypes.NEWS,
    NotificationTypes.TAS,
    NotificationTypes.SEIZURE,
    NotificationTypes.FALL,
    NotificationTypes.EMERGENCY,
    NotificationTypes.SYMPTOM,
    NotificationTypes.CONVERSATION,
    NotificationTypes.ECG,
]
const InitialPageable = {
    pageRows: 10,
    pageNumber: 0,
    sortField: 'id',
    sortOrder: -1,
}

const Notifications = React.memo(({height = '380px', removeSpace = false}) => {
    useSignals()
    //#region Context
    const {user} = useAuth()
    const {userRole, notificationSetting} = useAppConfig()
    const {
        totalNotifications,
        fallNotifications,
        newsNotifications,
        emergencyNotifications,
        symptomNotifications,
        conversationNotifications,
        ecgNotifications,
        notificationPageNumber,

        getNotificationQueryKeys,
        onChangeNotificationPageNumber,
    } = useNotificationContext()
    //#endregion

    const pageable = useSignal({...InitialPageable})

    //#region Location URL
    const history = useHistory()
    const location = useLocation()
    //#endregion

    //#region Map
    const mapRef = useRef()
    //#endregion

    //#region Query
    const queryClient = useQueryClient()
    //#endregion

    //#region Language
    const {translateCommons, translateKeys} = TranslateUtils()
    //#endregion
    //#region Notification Setting
    const settingRef = useRef(null)
    const handleShowSetting = useCallback(() => {
        settingRef.current.showForm()
    }, [])
    //#endregion
    const activeIndex = useSignal(0)

    //#region Notifications CRUD
    const composeNotificationQueryKeys = useCallback(
        (type, pageNumber) => {
            return getNotificationQueryKeys(type, pageNumber)
        },
        [getNotificationQueryKeys],
    )

    const {mutate: fetchMoreNotifications} =
        useCustomPageNotificationsFetchMore(user?.id)

    const {mutate: readOrDiscardNotification} = useReadOrDiscardNotification(
        user?.id,
    )
    const handleFetchMoreData = useCallback(
        type => {
            const currentPage = notificationPageNumber[type]

            onChangeNotificationPageNumber(type)

            const filterRequest = composeNotificationQueryKeys(
                type,
                currentPage + 1,
            )

            fetchMoreNotifications({
                hospitalId: filterRequest?.hospitalId,
                type: filterRequest?.type,
                timezoneId: filterRequest?.timezoneId,
                pageNumber: filterRequest?.pageNumber,
                pageRows: filterRequest?.pageRows,
                sortField: filterRequest?.sortField,
                sortOrder: filterRequest?.sortOrder,
                filterRequest: filterRequest?.filterRequest,
                actionType: QueryActionTypes.append,
            })
        },
        [
            notificationPageNumber,
            onChangeNotificationPageNumber,
            composeNotificationQueryKeys,
            fetchMoreNotifications,
        ],
    )
    const handleDiscardNotification = item => {
        const notificationQueryKeys = composeNotificationQueryKeys(
            item?.notificationType,
            0,
        )

        let toPostData = {
            id: item?.id,
            status: 'discarded',
            notificationQueryKeys: notificationQueryKeys,
        }

        readOrDiscardNotification(toPostData)
    }

    const handleOnClickNotification = item => {
        const notificationQueryKeys = composeNotificationQueryKeys(
            item?.notificationType,
            0,
        )

        let toPostData = {
            id: item?.id,
            status: NotificationStatus.read,
            notificationQueryKeys: notificationQueryKeys,
        }

        readOrDiscardNotification(toPostData)
        let url = null
        switch (item?.notificationType) {
            case NotificationTypes.CONVERSATION:
                url = history.push(`/conversations/${item?.roomId}`)
                if (item?.roomId) {
                    setRoomMessage(item?.roomId)
                }
                break
            case NotificationTypes.ECG:
                url = `/patients/${item?.userId}?tv=statistic`
                break
            case NotificationTypes.SYMPTOM:
                url = `/patients/${
                    item?.userId
                }?tv=observation&isOpenSymptomForm=${true}`
                break
            default:
                url = `/patients/${item?.userId}?tv=observation`
                break
        }
        if (url) {
            history.push(url)
        }
    }
    //#endregion

    //#region Notifications
    const width = useWindowSize()
    const notificationList = useMemo(() => {
        return headerNotificationList()
    }, [])
    const iconsSmallList = useMemo(() => {
        return headerNotificationList('20px', 'mr-2')
    }, [])

    const notificationData = useMemo(() => {
        return {
            news: newsNotifications || {},
            fall: fallNotifications || {},
            emergency: emergencyNotifications || {},
            symptom: symptomNotifications || {},
            conversation: conversationNotifications || {},
            ecg: ecgNotifications || {},
        }
    }, [
        newsNotifications,
        fallNotifications,
        emergencyNotifications,
        symptomNotifications,
        conversationNotifications,
        ecgNotifications,
    ])

    const renderHeader = useCallback(
        key => {
            const value =
                notificationData &&
                notificationData[key] &&
                notificationData[key]?.totalUnseen
                    ? notificationData[key]?.totalUnseen
                    : 0
            return (
                <div className="flex gap-1 align-items-center">
                    {notificationList[key]?.icon}
                    <h6 className="m-0 p-0">
                        {translateCommons(notificationList[key]?.label)}
                    </h6>

                    {value > 0 && (
                        <Badge
                            value={value}
                            className="ml-1"
                            severity={value > 0 ? 'danger' : 'secondary'}
                        />
                    )}
                </div>
            )
        },
        [notificationData, notificationList, translateCommons],
    )

    const renderNotificationListItem = item => {
        return (
            <div className="flex align-items-center">
                <div className="w-3rem h-3rem flex align-items-center justify-content-center flex-shrink-0">
                    {iconsSmallList[item?.notificationType]?.icon}
                </div>
                <span className="text-900 line-height-3 font-bold font-medium">
                    <span
                        className="hover:underline"
                        onClick={() => {
                            handleOnClickNotification(item)
                        }}>
                        {' '}
                        {`${item?.firstName}`}
                    </span>
                    <span className="text-700 font-normal">
                        {' ' + translateCommons('has')}
                        <span className="text-primary font-medium">
                            {` ${
                                item?.notificationType ===
                                    NotificationTypes.NEWS && item?.score
                                    ? `${parseInt(
                                          item.score,
                                      )} ${translateCommons('on')} `
                                    : ''
                            } ${
                                item?.notificationType
                                    ? upperCase(
                                          translateCommons(
                                              item.notificationType,
                                          ),
                                      )
                                    : ''
                            } ${translateCommons('at')}  ${
                                item?.createdAt
                            }`}{' '}
                        </span>
                    </span>
                </span>
            </div>
        )
    }

    const convertToProperWatchName = input => {
        if (!input) {
            return 'Unknown'
        }

        try {
            // Split the input string by the underscore character
            let words = input.split('_')

            // Capitalize the first letter of each word and join them with a space
            for (let i = 0; i < words.length; i++) {
                words[i] =
                    words[i].charAt(0).toUpperCase() +
                    words[i].slice(1).toLowerCase()
            }

            // Join the words back together with a space between them
            return words.join(' ')
        } catch (error) {
            return 'Unknown'
        }
    }

    const headerTemplate = (options, item) => {
        const toggleIcon = options.collapsed
            ? 'pi pi-sort-down'
            : 'pi pi-sort-up'
        let readByHcps = []
        if (!!item.readByHCPs) {
            try {
                let convertStringToList = JSON.parse(item.readByHCPs)
                if (isArrayAndNotEmpty(convertStringToList)) {
                    readByHcps = convertStringToList
                }
            } catch {}
        }
        return (
            <React.Fragment>
                <div
                    className="flex align-items-center justify-content-between border-1 border-gray-50 hover:border-teal-200  border-md border-round-lg pb-2"
                    style={{
                        background:
                            item.status === NotificationStatus.read
                                ? ''
                                : 'var(--gray-50)',
                        // borderRadius: '8px',
                    }}>
                    <div
                        className="flex align-items-center p-2 w-full "
                        onClick={options.onTogglerClick}>
                        <span className="pointer hover:surface-100 border-circle p-2">
                            <span className={toggleIcon}></span>
                            <Ripple />
                        </span>
                        <div className={`flex align-items-center pointer mr-1`}>
                            {renderNotificationListItem(item)}
                        </div>
                        {readByHcps.length > 0 && (
                            <i
                                id={`${item.notificationType}-${item.id}`}
                                className="pi pi-eye px-2"
                            />
                        )}

                        <Tooltip
                            position="bottom"
                            style={{background: 'white'}}
                            target={`#${item.notificationType}-${item.id}`}>
                            {' '}
                            {readByHcps.map((hcp, hcpIdx) => (
                                <div
                                    className="flex mb-1 gap-2 bg-gray-50 p-2 border-round-sm"
                                    style={{minWidth: '15rem'}}
                                    key={hcpIdx}>
                                    <div>
                                        <Avatar
                                            shape="circle"
                                            size="large"
                                            image={hcp.avatar}
                                        />
                                    </div>{' '}
                                    <div>
                                        {' '}
                                        <h6 className="mb-1">
                                            {hcp.firstName} {hcp.lastName}
                                        </h6>
                                        <Tag value={hcp.roleName} />
                                    </div>
                                </div>
                            ))}
                        </Tooltip>
                    </div>

                    <div className="flex gap-3 px-3 align-items-center text-gray-700">
                        <Button
                            className={``}
                            style={{
                                background: 'rgba(239,68,69,0.05)',
                                borderColor: 'rgba(239,68,69,0.01)',
                                color: 'rgba(239,68,69,1)',
                            }}
                            onClick={() => {
                                handleDiscardNotification(item)
                            }}
                            icon={'pi pi-times'}></Button>
                        <Button
                            className="bg-blue-50 text-blue-500 border-blue-50"
                            icon="pi pi-arrow-right"
                            onClick={() => {
                                handleOnClickNotification(item)
                            }}
                        />
                    </div>
                </div>
            </React.Fragment>
        )
    }

    const {currentHospital} = useUserHospital()

    const renderNotificationTabPanel = parentKey => {
        const healthKey = [
            NotificationTypes.NEWS,
            NotificationTypes.TAS,
            NotificationTypes.EMERGENCY,
        ]

        const messageKey = [
            NotificationTypes.SYMPTOM,
            NotificationTypes.CONVERSATION,
        ]

        const handleOnClearAll = async parentKey => {
            const filterRequest = {
                type: parentKey,
                hospitalId: currentHospital?.id,
            }

            const res = await clearAllNotifications(user?.id, filterRequest)

            if (res === HTTP_STATUS_OK) {
                queryClient.invalidateQueries(QueryKeyTypes.notification)
                showNotification(severityNotification.success, 'Clear success')
            }
        }
        return (
            <TabPanel
                key={parentKey}
                id="notification-tab-panel"
                contentClassName="p-0 h-full"
                className="test"
                header={renderHeader(parentKey)}>
                {notificationData &&
                notificationData[parentKey] &&
                notificationData[parentKey]?.content?.length > 0 ? (
                    <React.Fragment>
                        <div className="flex justify-content-end">
                            <p
                                className=" py-2 font-semibold hover:text-blue-500 cursor-pointer hover:underline"
                                onClick={() => handleOnClearAll(parentKey)}>
                                Clear all
                            </p>
                        </div>
                        {notificationData[parentKey]?.content?.map(
                            (item, idx) => (
                                <div
                                    key={`${parentKey}-${item?.id}-content`}
                                    className="mb-1">
                                    {healthKey.includes(
                                        item?.notificationType,
                                    ) ? (
                                        <Panel
                                            key={`${parentKey}-${item?.id}-field`}
                                            headerTemplate={options =>
                                                headerTemplate(options, item)
                                            }
                                            collapsed
                                            toggleable
                                            className="align-items-center m-0 p-0 col-12"
                                            style={{
                                                border: 'none',
                                            }}>
                                            <div className="grid">
                                                {item?.notificationType ===
                                                    parentKey &&
                                                item[parentKey]?.fields
                                                    ?.length > 0 ? (
                                                    item[
                                                        parentKey
                                                    ]?.fields?.map(
                                                        childItem => (
                                                            <div
                                                                key={`${parentKey}-${item?.id}-ChildItem-${childItem.key}`}
                                                                className="col-6  p-1 m-0">
                                                                <div className="flex align-items-center bg-gray-50 border-round-left-3xl border-round-right-lg p-1">
                                                                    {' '}
                                                                    <div className="w-3rem h-3rem flex align-items-center justify-content-center bg-white border-circle mr-3 flex-shrink-0">
                                                                        {
                                                                            iconsSmallList[
                                                                                childItem
                                                                                    ?.key
                                                                            ]
                                                                                ?.icon
                                                                        }
                                                                    </div>
                                                                    <span className="text-900 line-height-3 font-bold font-medium">
                                                                        <span className="text-700 font-normal">
                                                                            {`${translateKeys(
                                                                                convertKeyToLabel(
                                                                                    childItem.key,
                                                                                ),
                                                                            )}: `}
                                                                            <span className="text-primary font-medium">
                                                                                {`${childItem.value}`}
                                                                                {`${
                                                                                    !!childItem.data
                                                                                        ? ` - ${translateCommons(
                                                                                              childItem.data,
                                                                                          )}`
                                                                                        : ''
                                                                                }`}
                                                                            </span>
                                                                        </span>
                                                                    </span>{' '}
                                                                </div>
                                                            </div>
                                                        ),
                                                    )
                                                ) : (
                                                    <div></div>
                                                )}
                                            </div>
                                        </Panel>
                                    ) : messageKey.includes(
                                          item?.notificationType,
                                      ) ? (
                                        <div
                                            className={`flex align-items-center gap-2 col-12 border-1 ${
                                                item.status ===
                                                NotificationStatus.unseen
                                                    ? 'bg-gray-50 border-gray-50 hover:border-gray-100'
                                                    : 'border-white hover:border-gray-100'
                                            }  cursor-pointer`}
                                            onClick={() => {
                                                handleOnClickNotification(item)
                                            }}>
                                            <div className="flex justify-content-center w-4rem relative ">
                                                <Avatar
                                                    className="p-overlay-badge text-blue-500"
                                                    label={
                                                        item?.firstName[0] ||
                                                        'V'
                                                    }
                                                    size="large"
                                                    shape="circle"
                                                />
                                                {item.status ===
                                                    NotificationStatus.unseen &&
                                                    item?.unreadCount > 0 && (
                                                        <Badge
                                                            className="absolute top-0 right-0"
                                                            value={
                                                                item?.unreadCount ||
                                                                0
                                                            }
                                                        />
                                                    )}
                                            </div>
                                            <div className="col flex justify-content-between">
                                                <div>
                                                    <div className="text-900 line-height-3 font-bold">
                                                        {`${item?.firstName}`}
                                                        <span className="text-700 font-normal">
                                                            {' '}
                                                            {translateCommons(
                                                                'has',
                                                            )}
                                                            <span className="text-primary font-medium">
                                                                {`${
                                                                    item?.notificationType
                                                                        ? upperCase(
                                                                              translateCommons(
                                                                                  item?.notificationType,
                                                                              ),
                                                                          )
                                                                        : ''
                                                                } ${translateCommons(
                                                                    'at',
                                                                )} ${
                                                                    item?.createdAt
                                                                }`}{' '}
                                                            </span>
                                                        </span>
                                                    </div>
                                                    <p className="w-15rem overflow-hidden text-overflow-ellipsis white-space-nowrap">
                                                        {item.content}
                                                    </p>
                                                </div>
                                                <div>
                                                    <Button icon="pi pi-arrow-right" />{' '}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div
                                            className={`flex align-items-center gap-2 col-12 border-1 ${
                                                item.status ===
                                                NotificationStatus.unseen
                                                    ? 'bg-gray-50 border-gray-50 hover:border-gray-100'
                                                    : 'border-white hover:border-gray-100'
                                            }  cursor-pointer`}
                                            onClick={() =>
                                                item?.notificationType ===
                                                'fall'
                                                    ? mapRef.current.showForm(
                                                          item[parentKey],
                                                      )
                                                    : handleOnClickNotification(
                                                          item,
                                                      )
                                            }
                                            style={{
                                                borderRadius: '8px',
                                            }}>
                                            <div className="flex align-items-center col-10">
                                                <div className="w-3rem flex align-items-center justify-content-center">
                                                    {
                                                        iconsSmallList[
                                                            item
                                                                ?.notificationType
                                                        ]?.icon
                                                    }
                                                </div>
                                                {item?.notificationType ===
                                                'ecg' ? (
                                                    <span className="text-900 line-height-3 font-bold">
                                                        {`${item?.firstName}`}
                                                        <span className="text-700 font-semibold">
                                                            {' '}
                                                            {translateCommons(
                                                                'has',
                                                            )}{' '}
                                                            received a new
                                                            <span className="text-primary ">
                                                                {` ${
                                                                    item?.notificationType
                                                                        ? upperCase(
                                                                              translateCommons(
                                                                                  item?.notificationType,
                                                                              ),
                                                                          )
                                                                        : ''
                                                                } from
                                                        their ${convertToProperWatchName(
                                                            item.ecg?.brand,
                                                        )}`}{' '}
                                                                <span className="text-700 ">
                                                                    at{' '}
                                                                </span>
                                                                <span className="text-blue-500">
                                                                    {
                                                                        item?.createdAt
                                                                    }
                                                                </span>
                                                            </span>
                                                        </span>
                                                    </span>
                                                ) : (
                                                    <span className="text-900 line-height-3 font-bold">
                                                        {`${item?.firstName}`}
                                                        <span className="text-700 font-normal">
                                                            {' '}
                                                            {translateCommons(
                                                                'has',
                                                            )}
                                                            <span className="text-primary font-medium">
                                                                {`${
                                                                    item?.notificationType
                                                                        ? upperCase(
                                                                              translateCommons(
                                                                                  item?.notificationType,
                                                                              ),
                                                                          )
                                                                        : ''
                                                                } ${translateCommons(
                                                                    'at',
                                                                )} ${
                                                                    item?.createdAt
                                                                }`}{' '}
                                                            </span>
                                                        </span>
                                                    </span>
                                                )}
                                            </div>

                                            <div className="col-2 p-0 flex justify-content-end">
                                                <Button
                                                    className={`p-button-text hover:bg-orange-50 border-circle p-2`}
                                                    style={{
                                                        color: '#EA5455',
                                                    }}
                                                    onClick={() => {
                                                        handleDiscardNotification(
                                                            item,
                                                        )
                                                    }}
                                                    icon={'pi pi-times'}>
                                                    <Ripple />
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ),
                        )}

                        {!notificationData[parentKey]?.last ? (
                            <div className="flex justify-content-center align-items-center">
                                <Button
                                    icon="pi pi-arrow-down"
                                    label={translateCommons('Show more')}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                    }}
                                    onClick={() => {
                                        handleFetchMoreData(parentKey)
                                    }}
                                    className="flex text-primary border-none flex-column-reverse gap-2 align-items-center justify-content-center-50"></Button>
                            </div>
                        ) : (
                            <div
                                className="col-12 my-3"
                                style={{
                                    height: '30px',
                                    backgroundColor: '#f9f9f9',
                                }}>
                                <p className="text-center font-bold">
                                    {translateCommons('No more data')}
                                </p>
                            </div>
                        )}
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <div className="flex p-3 px-5 justify-content-center align-items-center h-full h-30rem">
                            <div>
                                <img
                                    style={{width: '100%', height: '150px'}}
                                    src={NotifyImg}
                                    alt="No notifications"
                                />
                                <p className="font-bold text-center py-4">
                                    {translateCommons('No notifications')}
                                </p>
                            </div>
                        </div>
                    </React.Fragment>
                )}
            </TabPanel>
        )
    }
    const containsHealthCriteriaKey = useMemo(() => {
        let holderList = []

        if (isArrayAndNotEmpty(notificationSetting?.healthCriteriaList)) {
            for (const key of notificationTypeKey) {
                if (notificationSetting?.healthCriteriaList?.includes(key)) {
                    holderList.push(key)
                }
            }
        }
        return holderList
    }, [notificationSetting?.healthCriteriaList])

    return (
        <React.Fragment>
            <div
                className={`col-12 h-full p-0 main-radius ${
                    location.pathname === '/' && !removeSpace
                        ? 'sub-border-color p-3'
                        : 'p-0'
                }`}>
                <div className="surface-card border-round p-0 col-12 h-full">
                    <div className="flex justify-content-between align-items-center col-12">
                        <div className="flex flex-column">
                            <h4 className="mb-0 font-bold text-darkcyan">
                                {translateCommons('Notifications')}
                            </h4>
                        </div>
                        <div
                            className="flex align-items-center pointer bg-gray-50 p-2 border-1 border-gray-50 hover:bg-gray-100 border-round-sm"
                            onClick={handleShowSetting}>
                            <FcSettings
                                style={{width: '20px', height: '20px'}}
                            />
                            <Ripple />
                        </div>
                    </div>
                    {!!totalNotifications ? (
                        <div
                            className={`pointer col-12 p-0 overflow-auto`}
                            style={
                                width >= 950
                                    ? {height: '450px'}
                                    : {
                                          height: '70%',
                                      }
                            }>
                            {notificationSetting?.healthCriteriaList?.length >
                            0 ? (
                                <div>
                                    <TabView
                                        activeIndex={activeIndex.value}
                                        onTabChange={e => {
                                            activeIndex.value = e.index
                                        }}
                                        scrollable
                                        renderActiveOnly
                                        className="notification-tabview-wrapper p-1">
                                        {containsHealthCriteriaKey.map(
                                            (key, idx) =>
                                                renderNotificationTabPanel(
                                                    key,
                                                    idx,
                                                ),
                                        )}
                                        {/* {notificationTypeKey.map(
                                            (key, idx) =>
                                                notificationSetting?.healthCriteriaList?.includes(
                                                    key,
                                                ) &&
                                                renderNotificationTabPanel(
                                                    key,
                                                    idx,
                                                ),
                                        )} */}
                                    </TabView>
                                    {/* {notificationTypeKey.map(key => (
                                        <div key={`${key}-wrapper`}>
                                            {notificationSetting?.healthCriteriaList?.includes(
                                                key,
                                            ) && renderGroupNotification(key)}
                                        </div>
                                    ))}{' '} */}
                                </div>
                            ) : (
                                <div className="p-4 text-center ">
                                    <div className="flex p-3 px-5 justify-content-center col-12">
                                        <img width={'350px'} src={NotifyImg} />
                                    </div>
                                    <h5 className="mb-2 font-semibold ">
                                        {translateCommons(
                                            'Currently, no notifications found. Please check your settings',
                                        )}{' '}
                                    </h5>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex align-items-center justify-content-center py-7">
                            <img
                                src={require('../../assets/images/No-Notification.png')}
                                alt="No Notifications"
                                height={height}
                            />
                        </div>
                    )}
                </div>
            </div>
            <Map ref={mapRef} />
            <NotificationSetting ref={settingRef} />
        </React.Fragment>
    )
})

export default Notifications
